/**
 * Weekly, shareable snapshots of the Palworld TCG Price Index.
 *
 * A snapshot is addressed by its ISO week (e.g. `2026-W36`), so the link a user
 * generates today keeps resolving to the same week forever and two people who
 * share "this week's index" produce the identical URL. Nothing is stored: the
 * snapshot is derived from the same daily price board the index itself uses.
 */
import type { PriceRow } from "./prices-shared";
import { buildIndexBoard, type IndexPoint } from "./price-index.shared";

export interface WeekRange {
  key: string;
  /** Inclusive ISO dates (Monday .. Sunday). */
  start: string;
  end: string;
  label: string;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** ISO-8601 week key for a date, e.g. `2026-W36`. */
export function isoWeekKey(date: Date = new Date()): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** Parses `YYYY-Wnn` into its Monday–Sunday date range, or null when invalid. */
export function parseWeekKey(key: string): WeekRange | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(key ?? "");
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  if (year < 2024 || year > 2100 || week < 1 || week > 53) return null;
  // Monday of ISO week 1 = the Monday of the week containing Jan 4th.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const monday1 = new Date(jan4);
  monday1.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() || 7) - 1));
  const start = new Date(monday1);
  start.setUTCDate(monday1.getUTCDate() + (week - 1) * 7);
  if (isoWeekKey(start) !== key) return null;
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
  return { key, start: iso(start), end: iso(end), label: `${fmt(start)} – ${fmt(end)}` };
}

export interface SnapshotMover {
  code: string;
  name: string;
  set: string;
  rarity: string;
  image: string;
  market: number;
  change: number;
}

export interface IndexSnapshot {
  week: WeekRange;
  /** Index points that fall inside the week (may be shorter than 7). */
  points: IndexPoint[];
  level: number;
  change: number;
  capLevel: number;
  capChange: number;
  marketCap: number;
  basket: number;
  constituents: number;
  advancing: number;
  declining: number;
  gainers: SnapshotMover[];
  losers: SnapshotMover[];
  /** False when the price board has no data inside the requested week. */
  hasData: boolean;
}

const pct = (from: number, to: number) => (from > 0 ? Math.round(((to - from) / from) * 1000) / 10 : 0);

function moverFrom(row: PriceRow, start: string, end: string): SnapshotMover | null {
  const inWeek = row.history.filter((p) => p.day >= start && p.day <= end);
  if (inWeek.length < 2 || inWeek[0].value <= 0) return null;
  return {
    code: row.code,
    name: row.name,
    set: row.set,
    rarity: row.rarity,
    image: row.image,
    market: inWeek[inWeek.length - 1].value,
    change: pct(inWeek[0].value, inWeek[inWeek.length - 1].value),
  };
}

/** Builds the shareable snapshot for one ISO week from the daily price board. */
export function buildWeekSnapshot(rows: PriceRow[], week: WeekRange): IndexSnapshot {
  const board = buildIndexBoard(rows);
  const composite = board.composite;
  const cap = board.capWeighted;
  const points = (composite?.points ?? []).filter((p) => p.day >= week.start && p.day <= week.end);
  const capPoints = (cap?.points ?? []).filter((p) => p.day >= week.start && p.day <= week.end);

  const movers = rows
    .filter((r) => r.market > 0)
    .map((r) => moverFrom(r, week.start, week.end))
    .filter((m): m is SnapshotMover => m !== null)
    .sort((a, b) => b.change - a.change);

  const capValues = (composite?.capPoints ?? []).filter((p) => p.day >= week.start && p.day <= week.end);

  return {
    week,
    points,
    level: points.length ? points[points.length - 1].level : composite?.level ?? 0,
    change: points.length > 1 ? pct(points[0].level, points[points.length - 1].level) : 0,
    capLevel: capPoints.length ? capPoints[capPoints.length - 1].level : cap?.level ?? 0,
    capChange: capPoints.length > 1 ? pct(capPoints[0].level, capPoints[capPoints.length - 1].level) : 0,
    marketCap: capValues.length ? capValues[capValues.length - 1].level : composite?.marketCap ?? 0,
    basket: composite?.basket ?? 0,
    constituents: composite?.constituents ?? 0,
    advancing: movers.filter((m) => m.change > 0).length,
    declining: movers.filter((m) => m.change < 0).length,
    gainers: movers.filter((m) => m.change > 0).slice(0, 5),
    losers: movers.filter((m) => m.change < 0).slice(-5).reverse(),
    hasData: points.length > 0,
  };
}

/** The most recent complete week — the natural default for a shared snapshot. */
export function lastCompleteWeekKey(now: Date = new Date()): string {
  const d = new Date(now);
  d.setUTCDate(d.getUTCDate() - 7);
  return isoWeekKey(d);
}
